import type { Assistant } from "@inrev/assistant";
import { cn } from "@portal/components/lib/utils";
import {
	CITATION_TOOLTIP_PROPS,
	CitationTooltipCard,
	DefinitionList,
	EmptyState,
} from "@portal/components/patterns";
import { Banner, Card, ClickableText, Tag, Text } from "@portal/components/primitives";
import { PortaledTooltip } from "@portal/components/ui/Tooltip";
import { useId, useMemo, useRef, useState } from "react";
import { useOpenCitation } from "../../AdminAssistantCitationPreview";
import { documentCitation } from "../../assistantCitations";
import {
	ANSWER_LABELS,
	CONSOLIDATION_LABELS,
	entityTags,
	ownershipLabel,
	PARENT_BASIS_LABELS,
	periodSentence,
	RELATIONSHIP_LABELS,
} from "./assistantOrgChart";
import { OrgChartImageActions } from "./OrgChartImageActions";
import {
	append,
	BOX_HEIGHT,
	BOX_WIDTH,
	type DiagramEdge,
	type DiagramNode,
	type DiagramStack,
	orgChartDiagram,
	wrapName,
} from "./orgChartDiagram";
import { orgChartImageFileName } from "./orgChartImage";

type OrgChart = Assistant.Dtos.Agent.OrgChart.OrgChart;
type OrgChartEntity = Assistant.Dtos.Agent.OrgChart.Entity;
type Answer = Assistant.Dtos.Agent.OrgChart.Answer;
type Source = OrgChart["sources"][number];
type PageRef = OrgChartEntity["pages"][number];

const NAME_LINE_HEIGHT = 14;
/** How far below or above its box a stake's label sits, so a chip never touches the box. */
const LABEL_OFFSET = 20;
const LABEL_HEIGHT = 16;
/** About the width of one 11px semibold character, to size a label's chip. */
const LABEL_CHAR_WIDTH = 6.6;

/**
 * As in a hand-drawn structure chart, a box's outline is the one thing it says at a glance:
 * consolidated is a solid line, not consolidated is dashed, and not described is dotted.
 */
const OUTLINE: Record<Answer, { className: string; dash: string | undefined }> = {
	yes: { className: "stroke-bc-heavier", dash: undefined },
	no: { className: "stroke-bc-heavy", dash: "6 4" },
	not_described: { className: "stroke-bc-strong", dash: "2 3" },
};

const LEGEND: readonly Answer[] = ["yes", "no", "not_described"];

type Point = { x: number; y: number };

/**
 * Where each line meets its owned box. Several owners of one entity enter at separate points
 * along its top edge, in their left-to-right order, so their lines and labels never share a spot.
 */
const entryPoints = (edges: readonly DiagramEdge[], nodeById: ReadonlyMap<number, DiagramNode>) => {
	const points = new Map<DiagramEdge, Point>();
	const byOwned = new Map<number, DiagramEdge[]>();
	for (const edge of edges) append(byOwned, edge.ownedId, edge);
	for (const [ownedId, incoming] of byOwned) {
		const owned = nodeById.get(ownedId);
		if (owned === undefined) continue;
		const ordered = [...incoming].sort(
			(a, b) => (nodeById.get(a.ownerId)?.x ?? 0) - (nodeById.get(b.ownerId)?.x ?? 0),
		);
		ordered.forEach((edge, index) =>
			points.set(edge, {
				x: owned.x + (BOX_WIDTH * (index + 1)) / (ordered.length + 1),
				y: owned.y,
			}),
		);
	}
	return points;
};

/** A stake label as draw.io draws an edge label: on the line, on a chip that hides the line. */
const EdgeLabel = ({ text, at }: { text: string; at: Point }) => {
	const width = text.length * LABEL_CHAR_WIDTH + 8;
	return (
		<g transform={`translate(${at.x} ${at.y})`}>
			<rect
				x={-width / 2}
				y={-LABEL_HEIGHT / 2}
				width={width}
				height={LABEL_HEIGHT}
				rx={3}
				className="fill-surface-base"
			/>
			<text textAnchor="middle" dy={4} className="fill-primary text-[11px] font-semibold">
				{text}
			</text>
		</g>
	);
};

const Line = ({ edge, from, to }: { edge: DiagramEdge; from: Point; to: Point }) => {
	const along = Math.min(LABEL_OFFSET / Math.max(Math.abs(to.y - from.y), 1), 0.5);
	const [near, far] = edge.labelEnd === "owned" ? [to, from] : [from, to];
	const label = { x: near.x + (far.x - near.x) * along, y: near.y + (far.y - near.y) * along };
	return (
		<g data-testid="assistant-orgchart-line" data-kind={edge.kind}>
			<line
				x1={from.x}
				y1={from.y}
				x2={to.x}
				y2={to.y}
				strokeWidth={1}
				strokeDasharray={edge.kind === "stake" ? "4 3" : undefined}
				className={edge.kind === "parent" ? "stroke-bc-heavier" : "stroke-bc-strong"}
			/>
			{edge.pct === null ? null : <EdgeLabel text={edge.pct} at={label} />}
		</g>
	);
};

/**
 * A long list of subsidiaries as draw.io draws one: a drop from the parent to a crossbar, a
 * trunk down each column, and a short tick into each box, carrying its stake.
 */
const Stack = ({
	stack,
	parent,
	nodeById,
}: {
	stack: DiagramStack;
	parent: DiagramNode;
	nodeById: ReadonlyMap<number, DiagramNode>;
}) => {
	const dropX = parent.x + BOX_WIDTH / 2;
	const trunkXs = stack.columns.map((column) => column.trunkX);
	return (
		<g data-testid="assistant-orgchart-stack" className="stroke-bc-heavier" strokeWidth={1}>
			<line x1={dropX} y1={parent.y + BOX_HEIGHT} x2={dropX} y2={stack.busY} />
			<line
				x1={Math.min(dropX, ...trunkXs)}
				y1={stack.busY}
				x2={Math.max(dropX, ...trunkXs)}
				y2={stack.busY}
			/>
			{stack.columns.map((column) => {
				const members = column.memberIds.flatMap((id) => {
					const node = nodeById.get(id);
					return node === undefined ? [] : [node];
				});
				const lastMid = Math.max(...members.map((node) => node.y + BOX_HEIGHT / 2));
				return (
					<g key={column.trunkX}>
						<line x1={column.trunkX} y1={stack.busY} x2={column.trunkX} y2={lastMid} />
						{members.map((node) => (
							<line
								key={node.entity.id}
								x1={column.trunkX}
								y1={node.y + BOX_HEIGHT / 2}
								x2={node.x}
								y2={node.y + BOX_HEIGHT / 2}
							/>
						))}
					</g>
				);
			})}
		</g>
	);
};

const Box = ({
	node,
	selected,
	stackedPct,
	tooltipId,
	onSelect,
}: {
	node: DiagramNode;
	selected: boolean;
	/** A stacked subsidiary's stake, which has no line of its own to sit on. */
	stackedPct: string | null;
	/** The one hover card every box shares; it reads this box's entity from `data-entity-id`. */
	tooltipId: string;
	onSelect: () => void;
}) => {
	const { entity } = node;
	const outline = OUTLINE[entity.consolidated];
	const lines = wrapName(entity.name);
	const firstBaseline = BOX_HEIGHT / 2 - ((lines.length - 1) * NAME_LINE_HEIGHT) / 2 + 4;
	const marked = entity.subsequentEvent !== null || entity.changeSincePrior === "new";

	return (
		<g
			transform={`translate(${node.x} ${node.y})`}
			role="button"
			tabIndex={0}
			aria-label={`${entity.name}, ${CONSOLIDATION_LABELS[entity.consolidated].toLowerCase()}`}
			aria-pressed={selected}
			onClick={onSelect}
			onKeyDown={(event) => {
				if (event.key === "Enter" || event.key === " ") {
					event.preventDefault();
					onSelect();
				}
			}}
			className="group cursor-pointer outline-none"
			data-testid="assistant-orgchart-entity"
			data-tooltip-id={tooltipId}
			data-entity-id={entity.id}
			data-consolidated={entity.consolidated}
		>
			<rect
				width={BOX_WIDTH}
				height={BOX_HEIGHT}
				strokeWidth={selected ? 2 : 1}
				strokeDasharray={outline.dash}
				className={cn(
					"fill-surface-base group-focus-visible:stroke-brand-accent group-focus-visible:[stroke-width:2]",
					selected ? "stroke-brand-accent" : outline.className,
				)}
			/>
			{entity.vie === "yes" ? (
				<text
					x={BOX_WIDTH - 6}
					y={12}
					textAnchor="end"
					className="fill-secondary text-[9px] font-semibold"
				>
					VIE
				</text>
			) : null}
			{marked ? <circle cx={8} cy={8} r={3} className="fill-brand-accent" aria-hidden /> : null}
			<text textAnchor="middle" className="fill-primary text-[12px]">
				{lines.map((line, index) => (
					<tspan key={line} x={BOX_WIDTH / 2} y={firstBaseline + index * NAME_LINE_HEIGHT}>
						{line}
					</tspan>
				))}
			</text>
			{stackedPct === null ? null : (
				<EdgeLabel text={stackedPct} at={{ x: BOX_WIDTH - 22, y: BOX_HEIGHT - 9 }} />
			)}
		</g>
	);
};

const PageLink = ({ refs, sources }: { refs: PageRef; sources: readonly Source[] }) => {
	const openCitation = useOpenCitation();
	const source = sources.find((candidate) => candidate.fileId === refs.fileId);
	const role =
		source === undefined
			? "Document"
			: source.role === "statement"
				? "Financial statements"
				: "Supporting document";
	const label = `${role}, p. ${refs.page}`;
	const full = `${source?.fileName ?? "Document"}, p. ${refs.page}`;
	const citation = documentCitation({
		kind: "file",
		id: refs.fileId,
		page: refs.page,
		title: full,
	});
	if (openCitation === undefined || citation === undefined) {
		return (
			<Text type="caption" color="tertiary" title={full}>
				{label}
			</Text>
		);
	}
	return (
		<ClickableText
			type="caption"
			color="link"
			title={full}
			aria-label={`Open ${full}`}
			onClick={() => openCitation(citation)}
		>
			{label}
		</ClickableText>
	);
};

/**
 * The reading behind one box. The reasoning leads, since it is what the underwriter opened the
 * box to read. Yes / No answers sit on inline rows. The parent basis is a sentence, so it stacks
 * under its label; an inline row in this 18rem rail breaks the word "Parent".
 */
const EntityDetail = ({
	entity,
	sources,
	entityById,
}: {
	entity: OrgChartEntity;
	sources: readonly Source[];
	entityById: ReadonlyMap<number, OrgChartEntity>;
}) => {
	const tags = entityTags(entity);
	const stake = ownershipLabel(entity, entityById);
	return (
		<div className="flex flex-col gap-3" data-testid="assistant-orgchart-entity-detail">
			<div className="flex flex-col gap-2">
				<Text type="body-strong" color="primary">
					{entity.name}
				</Text>
				{tags.length === 0 ? null : (
					<div className="flex flex-wrap gap-1.5">
						{tags.map((tag) => (
							<Tag key={tag.label} color={tag.color}>
								{tag.label}
							</Tag>
						))}
					</div>
				)}
			</div>
			<Text type="body" color="secondary">
				{entity.reasoning}
			</Text>
			<DefinitionList>
				<DefinitionList.Section title="Reading">
					{entity.parentBasis === null ? null : (
						<DefinitionList.Item label="Parent">
							{PARENT_BASIS_LABELS[entity.parentBasis]}
						</DefinitionList.Item>
					)}
					<DefinitionList.Item label="Consolidated" layout="inline">
						{ANSWER_LABELS[entity.consolidated]}
					</DefinitionList.Item>
					<DefinitionList.Item label="VIE" layout="inline">
						{ANSWER_LABELS[entity.vie]}
					</DefinitionList.Item>
					<DefinitionList.Item label="Common owner" layout="inline">
						{ANSWER_LABELS[entity.commonOwnerFullyOwned]}
					</DefinitionList.Item>
					{stake === null ? null : <DefinitionList.Item label="Stake">{stake}</DefinitionList.Item>}
					{entity.natureOfRelationship.length === 0 ? null : (
						<DefinitionList.Item label="Relationships">
							{entity.natureOfRelationship.map((key) => RELATIONSHIP_LABELS[key]).join(", ")}
						</DefinitionList.Item>
					)}
				</DefinitionList.Section>
			</DefinitionList>
			{entity.pages.length === 0 ? null : (
				<div className="flex flex-col gap-1" data-testid="assistant-orgchart-entity-pages">
					{entity.pages.map((refs) => (
						<PageLink key={`${refs.fileId}-${refs.page}`} refs={refs} sources={sources} />
					))}
				</div>
			)}
		</div>
	);
};

const LegendSwatch = ({ answer }: { answer: Answer }) => (
	<svg width={20} height={12} aria-hidden>
		<rect
			x={1}
			y={1}
			width={18}
			height={10}
			strokeWidth={1}
			strokeDasharray={OUTLINE[answer].dash}
			className={cn("fill-surface-base", OUTLINE[answer].className)}
		/>
	</svg>
);

/**
 * The full name a box truncates, and its stake, on hover. One card for every box, laid out as a
 * citation's hover card is, so the panel's hover cards all read alike.
 */
const BoxTooltip = ({
	id,
	entityById,
}: {
	id: string;
	entityById: ReadonlyMap<number, OrgChartEntity>;
}) => (
	<PortaledTooltip
		id={id}
		{...CITATION_TOOLTIP_PROPS}
		render={({ activeAnchor }) => {
			const entity = entityById.get(Number(activeAnchor?.getAttribute("data-entity-id")));
			return entity === undefined ? null : (
				<CitationTooltipCard
					kind={CONSOLIDATION_LABELS[entity.consolidated]}
					heading={entity.name}
					detail={ownershipLabel(entity, entityById) ?? undefined}
					testId="assistant-orgchart-entity-tooltip"
				/>
			);
		}}
	/>
);

const Legend = () => (
	<div className="flex flex-col gap-2" aria-hidden>
		{LEGEND.map((answer) => (
			<div key={answer} className="flex items-center gap-2">
				<LegendSwatch answer={answer} />
				<Text type="caption" color="tertiary">
					{CONSOLIDATION_LABELS[answer]}
				</Text>
			</div>
		))}
		<div className="flex items-center gap-2">
			<svg width={20} height={12} aria-hidden>
				<line x1={1} y1={6} x2={19} y2={6} strokeDasharray="4 3" className="stroke-bc-strong" />
			</svg>
			<Text type="caption" color="tertiary">
				Minority stake
			</Text>
		</div>
	</div>
);

/**
 * The corporate structure `ask_org_chart` read from an account's documents, drawn like a
 * draw.io structure chart: a box per entity, owners above what they hold, each line labeled with
 * the stake. Clicking a box opens its reading in the rail beside the diagram. Every box and line
 * is the tool's data, never anything the model wrote.
 */
export const AdminAssistantOrgChartView = ({ chart }: { chart: OrgChart }) => {
	const diagram = useMemo(() => orgChartDiagram(chart.entities), [chart.entities]);
	const [selectedId, setSelectedId] = useState<number | undefined>(undefined);
	const nodeById = useMemo(
		() => new Map(diagram.nodes.map((node) => [node.entity.id, node])),
		[diagram.nodes],
	);
	const entries = useMemo(() => entryPoints(diagram.edges, nodeById), [diagram.edges, nodeById]);
	const stacked = useMemo(
		() => new Set(diagram.stacks.flatMap((stack) => stack.columns.flatMap((c) => c.memberIds))),
		[diagram.stacks],
	);
	const entityById = useMemo(
		() => new Map(chart.entities.map((entity) => [entity.id, entity])),
		[chart.entities],
	);
	const selected = selectedId === undefined ? undefined : entityById.get(selectedId);
	const tooltipId = useId();
	const cardRef = useRef<HTMLDivElement>(null);

	return (
		<Card ref={cardRef} density="compact" data-testid="assistant-orgchart">
			<Card.Header className="items-start gap-3">
				<div className="flex min-w-0 flex-col items-start gap-1.5">
					<Card.Title>{chart.reportingEntity}</Card.Title>
					<Text type="caption" color="tertiary" data-testid="assistant-orgchart-period">
						{periodSentence(chart.periodEnd, chart.priorPeriodEnd)}
					</Text>
				</div>
				<OrgChartImageActions
					cardRef={cardRef}
					fileName={orgChartImageFileName(chart.reportingEntity)}
				/>
			</Card.Header>
			<Card.Body className="flex flex-col gap-3">
				{/*
				 * The panel runs wide for a structure chart, so the reading sits beside the diagram, as
				 * draw.io's format panel does. Dragged narrow, the rail wraps below it.
				 */}
				<div className="flex flex-wrap items-start gap-4">
					<div className="min-w-0 flex-[1_1_32rem] overflow-x-auto">
						<svg
							viewBox={`0 0 ${diagram.width} ${diagram.height}`}
							width="100%"
							style={{ minWidth: Math.min(diagram.width, 640), maxWidth: diagram.width }}
							className="mx-auto block"
							role="group"
							aria-label={`Organizational structure of ${chart.reportingEntity}`}
							data-testid="assistant-orgchart-diagram"
						>
							{diagram.edges.map((edge) => {
								const owner = nodeById.get(edge.ownerId);
								const to = entries.get(edge);
								return owner === undefined || to === undefined ? null : (
									<Line
										key={`${edge.ownerId}-${edge.ownedId}-${edge.kind}`}
										edge={edge}
										from={{ x: owner.x + BOX_WIDTH / 2, y: owner.y + BOX_HEIGHT }}
										to={to}
									/>
								);
							})}
							{diagram.stacks.map((stack) => {
								const parent = nodeById.get(stack.parentId);
								return parent === undefined ? null : (
									<Stack key={stack.parentId} stack={stack} parent={parent} nodeById={nodeById} />
								);
							})}
							{diagram.nodes.map((node) => (
								<Box
									key={node.entity.id}
									node={node}
									selected={node.entity.id === selectedId}
									stackedPct={stacked.has(node.entity.id) ? node.entity.ownershipPct : null}
									tooltipId={tooltipId}
									onSelect={() =>
										setSelectedId(node.entity.id === selectedId ? undefined : node.entity.id)
									}
								/>
							))}
						</svg>
					</div>
					<Card
						density="compact"
						className="flex-[0_0_18rem]"
						data-testid="assistant-orgchart-rail"
					>
						<Card.Body>
							{selected === undefined ? (
								<div className="flex flex-col gap-4">
									<EmptyState
										className="px-0 py-2"
										message="Select an entity to see the reading behind it."
										testId="assistant-orgchart-rail-empty"
									/>
									<Legend />
								</div>
							) : (
								<EntityDetail entity={selected} sources={chart.sources} entityById={entityById} />
							)}
						</Card.Body>
					</Card>
				</div>
				<BoxTooltip id={tooltipId} entityById={entityById} />

				{chart.removedSincePrior.length === 0 ? null : (
					<div className="flex flex-col gap-1.5" data-testid="assistant-orgchart-removed">
						<Text type="eyebrow" color="tertiary">
							Removed since prior FYE
						</Text>
						{chart.removedSincePrior.map((removed, index) => (
							// Names repeat, and a blank one is filled in the same way each time.
							<Text key={`${index}-${removed.name}`} type="caption" color="secondary">
								{`${removed.name}: ${removed.reasoning}`}
							</Text>
						))}
					</div>
				)}

				{chart.isComprehensive ? null : (
					<Banner tone="info" data-testid="assistant-orgchart-incomplete">
						<Text type="caption" color="secondary">
							{chart.completenessNote === null
								? "The documents suggest this list is incomplete."
								: `May be incomplete: ${chart.completenessNote}`}
						</Text>
					</Banner>
				)}
			</Card.Body>
		</Card>
	);
};
